// VerantwortlichChip.tsx — Kontaktverantwortlichkeit (FO/BO) als Pille im
// Pillen-Band der Detailseite. Das Band (`.verantrow`) liegt als eigene Zeile
// zwischen Titelzeile und Chip-Zeile; FO, BO und Status stehen darin
// nebeneinander:
//
//   [Foto]  Anna Beispiel
//   [ FO: Anna Beispiel ▾ ] [ BO: Team Leben ▾ ] [ Interessent ▾ ]
//   [ Privat ] [ ★★★★★ 3 ] [ Erstinformation und Datenschutz ] …
//
// Optik = Team-Pille der Kontakttabelle (`shared/foBo.tsx`, `FoBoBadge`):
// runde Kapsel, 11px/700, deterministische Hash-Farbe pro Zuordnung. Ohne
// Zuordnung bleibt die Pille neutral — dann aber in --ink-2 statt --ink-3, sonst
// steht der Leerzustand bei 2,94:1 unter der WCAG-AA-Schwelle von 4,5:1.
//
// Aufbau wie bei der Status-Pille (Trigger + Portal-Menü mit kvp-rd-Scrim,
// Position aus getBoundingClientRect), ergänzt um ein Suchfeld: die FO-Liste
// umfasst alle Frontoffice-Nutzer des Mandanten und ist zum Durchscrollen zu
// lang (Vorbild: VerantwortlichkeitDropdown).
//
// „Kontaktverantwortlicher FO/BO" ist bewusst ausgeschrieben — die Rollen aus
// `components/verantwortlichkeit/` heißen ebenfalls „Verantwortlichkeit",
// meinen aber die Zuständigkeit innerhalb einer Beziehung.
//
// Anders als die optisch gleichen Chips „Jahresgespräch"/„Empfehlung" schreibt
// die Auswahl hier SOFORT (PUT /contact/:id/ownership), nicht erst über die
// Speicherleiste. Das Menü sagt das ausdrücklich an.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";

import { hashColor } from "../shared/foBo";
import { aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/**
 * Menübreite in px — EINZIGE Quelle: sie geht als Inline-Breite ans Menü UND in
 * die Randberechnung von `openMenu`. `.verantchip-menu` setzt daher bewusst
 * keine eigene `width`; sonst könnte eine Änderung im CSS die Positionierung
 * unbemerkt verrutschen lassen.
 */
const MENU_W = 260;

export interface VerantwortlichOption {
  id: string;
  name: string;
  /** KMVS-Kürzel des Nutzers (z. B. „MK“) — durchsuchbar, grau hinter dem Namen. */
  kuerzel?: string | null;
}

/**
 * Trefferliste zur Sucheingabe. Rein, damit unit-testbar.
 *
 * Trifft Name UND Kürzel: Im Haus heißen die Kollegen „MK“ und „DC“, gesucht
 * wurde aber nur im Namen (T-164, 03.10.2026). Wer ein Kürzel exakt eintippt,
 * meint genau diesen Nutzer — der steht deshalb vor den übrigen Treffern, die
 * die Buchstabenfolge nur irgendwo im Namen oder in einem längeren Kürzel tragen.
 */
export function filterVerantOptionen<T extends VerantwortlichOption>(optionen: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return optionen;
  const kuerzelVon = (o: T) => (o.kuerzel ?? "").trim().toLowerCase();
  const treffer = optionen.filter((o) => o.name.toLowerCase().includes(q) || kuerzelVon(o).includes(q));
  const exakt = treffer.filter((o) => kuerzelVon(o) === q);
  return exakt.length ? [...exakt, ...treffer.filter((o) => kuerzelVon(o) !== q)] : treffer;
}

export interface VerantwortlichChipProps {
  /** Frontoffice-Nutzer oder Backoffice-Team. Bestimmt Präfix und Beschriftung. */
  art: "FO" | "BO";
  /** Angezeigter Name der aktuellen Zuordnung. Leer → `leerText`. */
  wert?: string;
  /** Id der aktuellen Zuordnung (Markierung im Menü). */
  gewaehlt?: string;
  /** Auswahlliste (FO-Nutzer bzw. BO-Teams). */
  optionen: VerantwortlichOption[];
  /** Text, wenn nichts zugeordnet ist — z. B. „Kein FO". */
  leerText: string;
  /** Wählbarer Eintrag zum Entfernen der Zuordnung (nur BO). */
  leerOption?: string;
  /**
   * Schlüssel der Hash-Farbe. Ohne Angabe färbt der angezeigte Name. Nur nötig,
   * wo die Kontakttabelle einen anderen Schlüssel nimmt (FO: die User-Id), damit
   * dieselbe Person hier und dort denselben Farbton trägt.
   */
  farbschluessel?: string;
  /** Nur mit `canReassignOwnership` — sonst statischer Chip ohne Menü. */
  editable?: boolean;
  /** Neue Zuordnung gewählt (leerer String = entfernen). Speichert sofort. */
  onChange?: (id: string) => void;
  /**
   * Abweichendes Präfix in der Pille (Default „FO:"/„BO:") und abweichende
   * Menü-Überschrift (Default „Kontaktverantwortlicher FO/BO") — für Pillen
   * derselben Bauart im Band, z. B. „Persönlicher Kontakt von".
   */
  praefix?: string;
  bezeichnung?: string;
}

export function VerantwortlichChip({
  art,
  wert,
  gewaehlt,
  optionen,
  leerText,
  leerOption,
  farbschluessel,
  editable,
  onChange,
  praefix,
  bezeichnung: bezeichnungProp,
}: VerantwortlichChipProps) {
  const [menuAt, setMenuAt] = React.useState<{ x: number; y: number } | null>(null);
  const [query, setQuery] = React.useState("");
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const sucheRef = React.useRef<HTMLInputElement>(null);

  const bezeichnung = bezeichnungProp ?? `Kontaktverantwortlicher ${art}`;
  const zugeordnet = (wert ?? "").trim();
  const anzeige = zugeordnet || leerText;
  // Die Pille kürzt lange Namen per Ellipsis — der volle Wert gehört deshalb in
  // den Tooltip (wie bei der Team-Pille der Kontakttabelle), ohne den Hinweis
  // auf die Änderbarkeit zu verlieren.
  const titel = `${bezeichnung}: ${anzeige}`;
  // Farbe exakt wie die Team-Pille der Kontakttabelle (shared/foBo).
  const farbe = zugeordnet ? hashColor(farbschluessel?.trim() || zugeordnet) : null;
  const stil = farbe ? { background: farbe.bg, color: farbe.fg } : undefined;
  const klasse = "verantpill" + (farbe ? "" : " leer");
  const treffer = filterVerantOptionen(optionen, query);
  // Die Leer-Option läuft durch dieselbe Suche wie die echten Einträge — sonst
  // stünde sie bei einer Sucheingabe ohne Treffer neben „Keine Treffer.".
  const leerTreffer =
    !!leerOption && filterVerantOptionen([{ id: "", name: leerOption }], query).length > 0;
  // Die Leer-Wahl ist nur dann die aktuelle, wenn wirklich nichts zugeordnet ist.
  // Ein Alt-BO vom Typ USER hat keine wählbare Id, trägt aber einen Namen.
  const leerAktiv = !gewaehlt && !(wert ?? "").trim();

  const openMenu = () => {
    if (!editable) return;
    // Anker ist die Pille selbst: im Band stehen FO und BO nebeneinander, jedes
    // Menü geht also linksbündig unter der eigenen Kapsel auf (wie bei der
    // Status-Pille). Die Chip-Zeile darunter überlagert es dabei — das ist bei
    // einem Aufklapp-Menü gewollt und endet mit dem Schließen.
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setQuery("");
    setMenuAt({ x: Math.max(8, Math.min(r.left, aktivesFenster().innerWidth - MENU_W - 8)), y: r.bottom + 6 });
  };

  const close = () => {
    setMenuAt(null);
    setQuery("");
  };

  const pick = (id: string) => {
    close();
    if (id !== (gewaehlt ?? "")) onChange?.(id);
  };

  React.useEffect(() => {
    if (menuAt) sucheRef.current?.focus();
  }, [menuAt]);

  const inhalt = (
    <>
      <span className="k">{praefix ?? `${art}:`}</span>
      <span className="n">{anzeige}</span>
      {editable && <span className="statuspill-caret">▾</span>}
    </>
  );

  if (!editable) {
    return (
      <span className={klasse + " static"} style={stil} title={titel}>
        {inhalt}
      </span>
    );
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={klasse + " verantpill-btn"}
        style={stil}
        onClick={openMenu}
        title={`${titel} — ändern`}
        aria-haspopup="menu"
      >
        {inhalt}
      </button>

      {menuAt &&
        createPortal(
          <div className="kvp-rd-scrim statuspill-scrim" onClick={close}>
            <div
              className="kvp-rd statuspill-menu verantchip-menu"
              style={{ left: menuAt.x, top: menuAt.y, width: MENU_W }}
              role="menu"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.stopPropagation();
                  close();
                }
              }}
            >
              <div className="verantchip-kopf">
                <span className="verantchip-titel">{bezeichnung}</span>
                <span className="verantchip-hinweis">Auswahl wird sofort gespeichert</span>
              </div>
              <input
                ref={sucheRef}
                className="verantchip-suche"
                value={query}
                placeholder="Suchen …"
                autoComplete="off"
                aria-label={`${bezeichnung} durchsuchen`}
                onChange={(e) => setQuery(e.target.value)}
              />
              <div className="verantchip-liste">
                {leerTreffer && (
                  <button
                    type="button"
                    role="menuitem"
                    className={"statuspill-item verantchip-item leer" + (leerAktiv ? " on" : "")}
                    onClick={() => pick("")}
                  >
                    {leerOption}
                  </button>
                )}
                {treffer.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    role="menuitem"
                    className={"statuspill-item verantchip-item" + (o.id === gewaehlt ? " on" : "")}
                    onClick={() => pick(o.id)}
                  >
                    {o.name}
                    {o.kuerzel && <span className="verantchip-kuerzel">{o.kuerzel}</span>}
                  </button>
                ))}
                {treffer.length === 0 && !leerTreffer && (
                  <div className="verantchip-leer">Keine Treffer.</div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
